import { useEffect } from 'react';

const SITE_NAME = 'MSA Sport';

function setOrCreate(selector, attr, value) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const m = selector.match(/\[(\w[\w-]*(?::\w[\w-]*)?)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

/**
 * useSeo — dynamically updates <title>, meta description, OG, Twitter, canonical.
 * Call at the top of any page component.
 *
 * @param {object} options
 * @param {string} [options.title]        — page title (appended with site name)
 * @param {string} [options.description]  — meta description (up to 160 chars)
 * @param {string} [options.keywords]     — comma-separated keywords
 * @param {string} [options.image]        — OG / Twitter image URL
 * @param {string} [options.url]          — canonical URL (defaults to current href)
 * @param {string} [options.type]         — OG type: "website" | "product" | "article"
 * @param {boolean} [options.noindex]     — true → noindex,nofollow
 */
export function useSeo({ title, description, keywords, image, url, type = 'website', noindex = false } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    document.title = fullTitle;

    const desc = description || '';
    const img = image || '';
    const href = url || window.location.href;

    // Basic
    setOrCreate('meta[name="description"]', 'content', desc);
    setOrCreate('meta[name="robots"]', 'content', noindex ? 'noindex,nofollow' : 'index,follow');
    if (keywords) setOrCreate('meta[name="keywords"]', 'content', keywords);

    // Open Graph
    setOrCreate('meta[property="og:title"]', 'content', fullTitle);
    setOrCreate('meta[property="og:description"]', 'content', desc);
    setOrCreate('meta[property="og:image"]', 'content', img);
    setOrCreate('meta[property="og:url"]', 'content', href);
    setOrCreate('meta[property="og:type"]', 'content', type);
    setOrCreate('meta[property="og:site_name"]', 'content', SITE_NAME);
    setOrCreate('meta[property="og:locale"]', 'content', 'uk_UA');

    // Twitter Card
    setOrCreate('meta[name="twitter:card"]', 'content', img ? 'summary_large_image' : 'summary');
    setOrCreate('meta[name="twitter:title"]', 'content', fullTitle);
    setOrCreate('meta[name="twitter:description"]', 'content', desc);
    if (img) setOrCreate('meta[name="twitter:image"]', 'content', img);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', href);

    return () => {
      document.title = SITE_NAME;
    };
  }, [title, description, keywords, image, url, type, noindex]);
}
